import BaseService from '../../base/base_service';
import Model from './regulation_model';
import RegulationFactory from './regulations/regulation_factory';
import { SYSTEM_EVENTS } from '../../events/events';
import DOM from '../../utils/dom';
import Device from "../../utils/device/device";
import USBaseRegulation from './regulations/us/us_base_regulation';

class RegulationService extends BaseService {
  /**
   * @static
   * @type {string}
   */
  static CLASS = 'RegulationService';

  /**
   * @property
   * @type {object|null}
   * @private
   */
  _model = null;

  _regulationSuffix = null;

  _regulationFactory = null;

  _initParams = null;

  _registeredBeforeCurrentLogin = false;

  /**
   *
   * @param params
   */
  init(params) {
    super.init(params);
    this._initParams = params;
    this._model = Model.create(params);
    this._regulationFactory = params.regulationFactory;
    this._subscribe();
  }

  _logginMessagesAlreadyShowed = false;

  /**
   * Subscribe regulation events.
   *
   * @private
   */
  _subscribe() {
    this.onSystemEvent(
      SYSTEM_EVENTS.ON_INIT_COMPLETE,
      () => {
        this._setRegulationCSS();

        if (this.serviceFactory.oAuth2Service().isAuthorized()) {
          this.getRegulation().startSessionTimer();
          this.getRegulation().showGamePopup();
        }
      }
    );

    this.onSystemEvent(
      SYSTEM_EVENTS.REGISTRATION_COMPLETED,
      () => {
        this._registeredBeforeCurrentLogin = true;
      }
    );

    this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, () => {
      if (!this._logginMessagesAlreadyShowed && this.serviceFactory.oAuth2Service().isAuthorized()) {
        if (this._registeredBeforeCurrentLogin === false) {
          this.getRegulation().showTempAccountMessage();
        }

        this.getRegulation().showLoginMessage();
        this._logginMessagesAlreadyShowed = true;
      }

      this._setRegulationCSS();

      if (this.serviceFactory.oAuth2Service().isAuthorized()) {
        this.getRegulation().showGamePopup();
        this.getRegulation().startSessionTimer();
      } else {
        this.getRegulation().stopSessionTimer();
        this._logginMessagesAlreadyShowed = false;
        this._registeredBeforeCurrentLogin = false;
      }
    });

    this.onSystemEvent(
      SYSTEM_EVENTS.SESSION_EVENT_KICK,
      data => {
        this.getRegulation().onKickEvent(data);
      }
    );

    this.onSystemEvent(SYSTEM_EVENTS.SECOND_WEB_VIEW_OPEN, () => {
      this._setSecondWebViewCSS(true);
    });

    this.onSystemEvent(SYSTEM_EVENTS.SECOND_WEB_VIEW_CLOSE, () => {
      this._setSecondWebViewCSS(false);
    });
  }

  /**
   * Set regulation css for HTML cgp-uk, cgp-sw...
   * @private
   */
  _setRegulationCSS() {
    let regSuffix = this.getRegulation().getCssSuffix();
    if (this._regulationSuffix) {
      DOM.removeClass(DOM.getHTML(), 'cgp-' + this._regulationSuffix);
    }
    DOM.addClass(DOM.getHTML(), 'cgp-' + regSuffix);
    this._regulationSuffix = regSuffix;
  }

  _setSecondWebViewCSS(isOpen) {
    let webViewClass = 'cgp-second-web-view-open';
    if (isOpen) {
      DOM.addClass(DOM.getHTML(), webViewClass);
    } else {
      DOM.removeClass(DOM.getHTML(), webViewClass);
    }
  }

  /**
   *
   * @returns {*}
   */
  getTimeLimitPercentageReminder() {
    return this._model.getTimeLimitPercentageReminder();
  }

  isGamePopupStepPass() {
    return this.getRegulation().isGamePopupStepPass();
  }

  getMaxDurationsInMinutesForMobile() {
    return this._model.getMaxDurationsInMinutesForMobile();
  }

  getMaxDurationsInMinutesForPC() {
    return this._model.getMaxDurationsInMinutesForPC();
  }

  getLossLimitPercentages() {
    return this._model.getLossLimitPercentages();
  }

  getIntervalReminderInMinutes() {
    return this._model.getIntervalReminderInMinutes();
  }

  getRestrictionPeriodsInMinutes() {
    return this._model.getRestrictionPeriodsInMinutes();
  }

  /**
   * Get reminder intervals appropriate to specific regulation
   *
   * @returns {Array}
   * @public
   *
   */
  getReminderIntervals() {
    return this._model.getReminderIntervals();
  }

  getSessionCooldownConfig() {
    return {
      cooldownTime: this._model.getSessionCooldownConfigData().cooldownTime,
      featureEnabled: this.isSessionCooldownEnabled()
    }
  }

  /**
   * Get current regulation code.
   * The function return regulation code appropriate to authorize status.
   * If user is authorized, then the code will be returned from oAuth data,
   * otherwise from Skin data
   *
   * @returns {string}
   * @public
   */
  getRegulationCode() {
    return this.serviceFactory.skinService().getRegulationID();
  }

  isUSRegulation() {
    return this.getRegulation() instanceof USBaseRegulation;
  }

  getRegulation() {
    if (!this._regulationFactory) {
      this._regulationFactory = RegulationFactory.create({
        serviceFactory: this.serviceFactory,
        configs: this._initParams,
      });
    }
    return this._regulationFactory.getRegulation(this.getRegulationCode());
  }

  showHelpInAboutWindow() {
    return this.getRegulation().showHelpInAboutWindow();
  }

  getClockLocalization() {
    return this.getRegulation().getClockLocalization();
  }

  getResponsibleGamingIconsComponent(params) {
    return this.getRegulation().getResponsibleGamingIconsComponent(params);
  }


  getErrorMessage(errorCode) {
    if (!errorCode) {
      this.throwError('222', 'Argument errorCode is required');
    }

    return this.getRegulation().getErrorMessage(errorCode);
  }

  isAnonymousPlayAllowed() {
    return this.getRegulation().isAnonymousPlayAllowed();
  }

  forceDemoButton() {
    return this.getRegulation().forceDemoButton();
  }

  stepsBeforeOpenLoginAndRegistration() {
    return this.getRegulation().stepsBeforeOpenLoginAndRegistration();
  }

  forceIntermediatePageForAutorizedUsers() {
    if(!Device.isMobile()) {
      return false;
    }

    return this.getRegulation().forceIntermediatePageForAutorizedUsers();
  }

  shouldPreventMoreThanOneOpenGame() {
    return this.getRegulation().shouldPreventMoreThanOneOpenGame();
  }

  getRealityCheckIntervalExpectedByTheGame() {
    return this.getRegulation().getRealityCheckIntervalExpectedByTheGame();
  }

  sessionTimerVisible() {
    return this.getRegulation().sessionTimerVisible();
  }

  getTempAccountGnwpSource() {
    return this.getRegulation().getTempAccountGnwpSource();
  }

  showSessionBalanceInAboutBox() {
    return this.getRegulation().showSessionBalanceInAboutBox();
  }

  shouldHaveBetLimitsPerBrand() {
    return this.getRegulation().shouldHaveBetLimitsPerBrand();
  }

  isSessionCooldownEnabled() {
    return this.getRegulation().isSessionCooldownEnabled();
  }

  //@TODO - this should be removed once the feature is approved by the regulator
  summaryMessageForPortugalEnabled() {
    return this._initParams.enableSummaryMessageForPortugal || false;
  }

  shouldShowPlayerLimitsToaster() {
    return this.getRegulation().shouldShowPlayerLimitsToaster();
  }

  fitToPlayModeEnabled() {
    return this.getRegulation().fitToPlayModeEnabled();
  }

  checkIfCorrectAppInUse(country, state) {
    return this.getRegulation().checkIfCorrectAppInUse(country, state);
  }

  shouldCallCountryByIP(country) {
    return this.getRegulation().shouldCallCountryByIP(country);
  }

  shouldRefreshTokenOnAppOpen() {
    return this.getRegulation().shouldRefreshTokenOnAppOpen();
  }
}

export default RegulationService;
